import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Calendar, Check } from "lucide-react";
import { toast } from "sonner";
import { CancelCallDialog } from "@/components/schedule/CancelCallDialog";


interface TimeSlot {
  date: string;
  time: string;
}

export default function ScheduleCall() {
  const { requestId } = useParams<{ requestId: string }>();
  const navigate = useNavigate();
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([
    { date: "", time: "" },
    { date: "", time: "" },
    { date: "", time: "" },
  ]);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);



  const { data: session, isLoading: sessionLoading } = useQuery({
    queryKey: ["schedule-call-session"],
    queryFn: async () => {
      const { data } = await supabase.auth.getSession();
      return data.session;
    },
  });

  const { data: schedulingRequest, isLoading, error, refetch } = useQuery({
    queryKey: ["scheduling-request", requestId],
    queryFn: async () => {
      if (!requestId) throw new Error("No request ID provided");

      const { data, error } = await supabase
        .from("scheduling_requests")
        .select(`
          *,
          requester:profiles!scheduling_requests_requester_id_fkey(full_name),
          recipient:profiles!scheduling_requests_recipient_id_fkey(full_name)
        `)
        .eq("id", requestId)
        .maybeSingle();

      if (error) throw error;
      if (!data) throw new Error("Scheduling request not found");
      return data;
    },
    enabled: !!requestId && !!session,
  });

  const proposedSlots: TimeSlot[] = Array.isArray(schedulingRequest?.selected_times)
    ? ((schedulingRequest?.selected_times as unknown as TimeSlot[]) ?? []).filter(
        (s) => s && s.date && s.time
      )
    : [];

  const confirmMutation = useMutation({
    mutationFn: async (isoValue: string) => {
      const scheduledAt = new Date(isoValue).toISOString();

      const { error: reqError } = await supabase
        .from("scheduling_requests")
        .update({ status: "confirmed" })
        .eq("id", requestId);
      if (reqError) throw reqError;

      if (schedulingRequest?.connection_id) {
        const { error: connError } = await supabase
          .from("connections")
          .update({ scheduled_at: scheduledAt })
          .eq("id", schedulingRequest.connection_id);
        if (connError) throw connError;
      }
    },
    onSuccess: () => {
      toast.success("Meeting confirmed!");
      refetch();
    },
    onError: (err: Error) => {
      toast.error("Failed to confirm meeting: " + err.message);
    },
  });




  const submitTimesMutation = useMutation({
    mutationFn: async (slots: TimeSlot[]) => {
      const validSlots = slots.filter(s => s.date && s.time);
      const { error } = await supabase
        .from("scheduling_requests")
        .update({
          selected_times: JSON.parse(JSON.stringify(validSlots)),
          status: "completed",
        })
        .eq("id", requestId);

      if (error) throw error;

      // Send notification email to the requester
      const { error: notifyError } = await supabase.functions.invoke("notify-times-submitted", {
        body: {
          schedulingRequestId: requestId,
          selectedTimes: validSlots,
        },
      });

      if (notifyError) {
        console.error("Failed to send notification:", notifyError);
        // Don't throw - the times were saved successfully
      }
    },
    onSuccess: () => {
      toast.success("Your available times have been submitted! The requester has been notified.");
      setTimeout(() => navigate("/"), 2000);
    },
    onError: (error) => {
      toast.error("Failed to submit times: " + error.message);
    },
  });

  const updateTimeSlot = (index: number, field: "date" | "time", value: string) => {
    const newSlots = [...timeSlots];
    newSlots[index][field] = value;
    setTimeSlots(newSlots);
  };

  const handleSubmit = () => {
    const validSlots = timeSlots.filter(s => s.date && s.time);
    if (validSlots.length === 0) {
      toast.error("Please select at least one available time");
      return;
    }
    submitTimesMutation.mutate(timeSlots);
  };

  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split("T")[0];
  };

  if (sessionLoading || (session && isLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary px-4">
        <Card className="max-w-md">
          <CardContent className="p-6 text-center space-y-4">
            <h2 className="text-xl font-semibold">Sign in to continue</h2>
            <p className="text-muted-foreground">
              Please sign in to your EAB Connect account to share your available times.
            </p>
            <Button
              onClick={() =>
                navigate(`/auth?redirect=${encodeURIComponent(`/schedule/${requestId}`)}`)
              }
            >
              Sign in
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !schedulingRequest) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary">
        <Card className="max-w-md">
          <CardContent className="p-6 text-center">
            <h2 className="text-xl font-semibold text-destructive mb-2">Link Invalid or Expired</h2>
            <p className="text-muted-foreground">This scheduling link is no longer valid.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isRequester = session.user.id === schedulingRequest.requester_id;

  const requesterName =
    (schedulingRequest.requester as { full_name: string | null } | null)?.full_name ||
    "Your connection";
  const recipientName =
    (schedulingRequest.recipient as { full_name: string | null } | null)?.full_name ||
    "Your connection";

  if (schedulingRequest.status === "cancelled") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary">
        <Card className="max-w-md">
          <CardContent className="p-6 text-center">
            <h2 className="text-xl font-semibold mb-2">Call Cancelled</h2>
            <p className="text-muted-foreground">
              This scheduling request was cancelled.
              {schedulingRequest.cancellation_reason
                ? ` Reason: ${schedulingRequest.cancellation_reason}`
                : ""}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Requester view: pick one of the proposed times, or cancel the call.
  if (isRequester) {
    return (
      <>
        <div className="min-h-screen bg-secondary py-12 px-4">
          <div className="max-w-lg mx-auto">
            <Card>
              <CardHeader className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Calendar className="h-8 w-8 text-primary" />
                </div>
                <CardTitle>Confirm Your Meeting</CardTitle>
                <CardDescription>
                  {proposedSlots.length > 0
                    ? `${recipientName} shared these available times. Choose one to confirm your call.`
                    : `${recipientName} has not submitted available times yet.`}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {schedulingRequest.status === "confirmed" && (
                  <div className="p-4 rounded-lg bg-muted/50 text-center">
                    <Check className="h-6 w-6 text-green-500 mx-auto mb-2" />
                    <p className="text-sm font-medium">This meeting is confirmed.</p>
                  </div>
                )}

                {proposedSlots.map((slot, index) => {
                  const value = `${slot.date}T${slot.time}`;
                  const selected = selectedSlot === value;
                  return (
                    <button
                      key={index}
                      type="button"
                      onClick={() => setSelectedSlot(value)}
                      className={`w-full text-left p-4 rounded-lg border transition-colors ${
                        selected ? "border-primary bg-primary/5" : "border-border bg-muted/30"
                      }`}
                    >
                      <span className="text-sm font-medium">
                        {new Date(value).toLocaleString("en-US", {
                          weekday: "long",
                          month: "short",
                          day: "numeric",
                          hour: "numeric",
                          minute: "2-digit",
                        })}
                      </span>
                    </button>
                  );
                })}

                {proposedSlots.length > 0 && schedulingRequest.status !== "confirmed" && (
                  <Button
                    className="w-full"
                    disabled={!selectedSlot || confirmMutation.isPending}
                    onClick={() => selectedSlot && confirmMutation.mutate(selectedSlot)}
                  >
                    {confirmMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Check className="h-4 w-4 mr-2" />
                    )}
                    Confirm Meeting
                  </Button>
                )}

                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setCancelOpen(true)}
                >
                  Cancel Call
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        <CancelCallDialog
          open={cancelOpen}
          onOpenChange={setCancelOpen}
          schedulingRequestId={schedulingRequest.id}
          connectionId={schedulingRequest.connection_id}
          otherPersonName={recipientName}
          otherPersonId={schedulingRequest.recipient_id}
          scheduledAt={selectedSlot}
          onCancelled={() => refetch()}
        />
      </>
    );
  }

  if (schedulingRequest.status === "completed" || schedulingRequest.status === "confirmed") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary">
        <Card className="max-w-md">
          <CardContent className="p-6 text-center">
            <Check className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Times Already Submitted</h2>
            <p className="text-muted-foreground">You have already submitted your available times for this request.</p>
          </CardContent>
        </Card>
      </div>
    );
  }



  return (
    <div className="min-h-screen bg-secondary py-12 px-4">
      <div className="max-w-lg mx-auto">
        <Card>
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Calendar className="h-8 w-8 text-primary" />
            </div>
            <CardTitle>Select Your Available Times</CardTitle>
            <CardDescription>
              {requesterName} would like to schedule a call with you. Please select up to 3 dates and times that work for your schedule.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {timeSlots.map((slot, index) => (
              <div key={index} className="p-4 bg-muted/50 rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Option {index + 1}</span>
                  {index > 0 && !slot.date && !slot.time && (
                    <span className="text-xs text-muted-foreground">Optional</span>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label htmlFor={`date-${index}`} className="text-xs">Date</Label>
                    <Input
                      id={`date-${index}`}
                      type="date"
                      min={getMinDate()}
                      value={slot.date}
                      onChange={(e) => updateTimeSlot(index, "date", e.target.value)}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor={`time-${index}`} className="text-xs">Time</Label>
                    <Input
                      id={`time-${index}`}
                      type="time"
                      value={slot.time}
                      onChange={(e) => updateTimeSlot(index, "time", e.target.value)}
                    />
                  </div>
                </div>
              </div>
            ))}

            <Button 
              onClick={handleSubmit} 
              className="w-full"
              disabled={submitTimesMutation.isPending}
            >
              {submitTimesMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Check className="h-4 w-4 mr-2" />
              )}
              Submit Available Times
            </Button>

            <p className="text-xs text-center text-muted-foreground">
              The requester will be notified of your availability and will confirm the final meeting time.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
